import React, { useEffect, useRef, useState } from 'react';
// https://github.com/JustFly1984/react-google-maps-api
// https://react-google-maps-api-docs.netlify.app/
import { Autocomplete, LoadScript } from "@react-google-maps/api";
import PropTypes from "prop-types";

const GOOGLE_MAPS_LIBRARIES = ['places'];

function getDisplayLocation(placeholder, value) {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  if (placeholder && placeholder.city && placeholder.state) {
    return `${placeholder.city}, ${placeholder.state}`;
  }

  return '';
}

/*
 * https://developers.google.com/maps/documentation/places/web-service/autocomplete#maps_http_places_autocomplete_amoeba_strict-js
 */
const PlaceComponent = ({ value, onChange = () => {}, placeholder, disabled, onPlaceChanged, handlePlaceChanged }) => {
  const inputRef = useRef();
  const [inputValue, setInputValue] = useState(() => getDisplayLocation(placeholder, value));

  useEffect(() => {
    setInputValue(getDisplayLocation(placeholder, value));
  }, [placeholder, value]);

  // https://developers.google.com/maps/documentation/javascript/place-autocomplete
  // https://developers.google.com/maps/documentation/javascript/supported_types
  const options = {
    componentRestrictions: { country: "us" },
    fields: ['address_components', 'formatted_address', 'geometry.location'],
    // types: ['(regions)'],  // to lock it to City/Town only (this isn't a great UX)
  };

  // Define the handler here, call prop if provided
  const handleAutocompleteChange = (autocompleteRef) => {
    if (autocompleteRef) {
      const place = autocompleteRef.getPlace();
      
      // Check if place is valid and has address components before proceeding
      if (!place || !place.address_components || place.address_components.length === 0) {
        console.log('No valid place selected or empty input, ignoring.');
        return;
      }

      setInputValue(place.formatted_address || getDisplayLocation(placeholder, value));
      
      // Now that we know we have a valid place, call the handlers
      if (onPlaceChanged) {
        onPlaceChanged(autocompleteRef);
      }
      
      // Also support handlePlaceChanged if provided
      if (handlePlaceChanged) {
        handlePlaceChanged(autocompleteRef);
      }
    }
  };

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
    onChange(event);
  };

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      libraries={GOOGLE_MAPS_LIBRARIES}
      preventGoogleFontsLoading
    >
      <Autocomplete
        onLoad={ref => inputRef.current = ref}
        onPlaceChanged={() => handleAutocompleteChange(inputRef.current)}
        options={options}
      >
        <input
          type="text"
          className="store-locator-location-input"
          name="store-locator-location"
          placeholder="Enter Location"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
          value={inputValue}
          onChange={handleInputChange}
          disabled={disabled}
          style={{
            backgroundImage: 'none',
            backgroundRepeat: 'no-repeat',
            WebkitAppearance: 'none',
            appearance: 'none'
          }}
        />
      </Autocomplete>
    </LoadScript>
  );
}

PlaceComponent.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      city: PropTypes.string,
      state: PropTypes.string
    })
  ]),
  disabled: PropTypes.bool,
  onPlaceChanged: PropTypes.func, // Optional, but recommended for parent integration
  handlePlaceChanged: PropTypes.func // Optional, newer API
};

export default PlaceComponent;
